/**
 * The fetched-Sleeper entry into the room report.
 *
 * 🔴 THIS FILE IS NOT THE PAGE ANY MORE (Sep 2 2026). RoomReport.jsx is, for
 * every source. What lives here is the Sleeper ADAPTER's half of the contract:
 * the copy only a fetched league can say (the benchmark cell and its small
 * sample and 2QB notes, read off the report's own meta), the share link only a
 * public Sleeper league has, and the click-through into one seat's own report.
 * The layout, the ranked cards, the badges and every rule about what may be
 * claimed on a room page moved to RoomReport, where the pasted room reads them
 * too. Two renderings of one report is two things to keep true.
 *
 * Every claim still names its own data (the three-mode lesson: prose never says
 * "above" or "below", it says which numbers). The benchmark label and the
 * small-sample and 2QB notes ship verbatim per the data rules in
 * docs/reference/rate-my-league-chunk-aug29.md, with the room counts read from
 * the benchmark meta rather than hardcoded prose that goes stale.
 *
 * 🔴 THE SEAT REPORT IS A DIFFERENT YARDSTICK AND THAT IS THE POINT. The room
 * page ranks a team against the eleven others in its own auction; opening a
 * seat measures that same roster against real 2026 auction teams. Both were
 * already built. Until now the only way into the second one from a Sleeper
 * league was to type the roster in again on the other tab.
 */

import { useMemo, useState } from "react";
import { theme } from "../../data/theme.js";
import { projectionIndex } from "../../utils/rateProjection.js";
import { rateTeam } from "../../utils/rateTeam.js";
import {
  seatEntriesFromSleeper,
  seatSettingsFromSleeper,
  sleeperRoomSource,
} from "../../utils/roomSource.js";
import { C, SZ, fineText } from "../Submit/submitStyles.js";
import EmailAsk from "./EmailAsk.jsx";
import RoomReport, { roomNavBtn } from "./RoomReport.jsx";
import TeamReportView from "./TeamReportView.jsx";

/** The way back out of a seat, the same affordance the pasted room's seats use. */
const backBtn = {
  padding: "8px 12px",
  fontSize: SZ.small,
  fontWeight: 800,
  borderRadius: theme.radius.lg,
  border: `1px solid ${C.border.brand}`,
  background: "transparent",
  color: C.brand.accent,
  cursor: "pointer",
};

export default function ReportView({ data, onReset }) {
  const r = data.report;
  const [copied, setCopied] = useState(false);
  // Which seat is open, by index into the leaderboard. Null is the room.
  const [seat, setSeat] = useState(null);

  // The whole room, normalised. Projected points are computed here, in the
  // browser, from the committed projection set and the league's own scoring.
  // When Sleeper gave us no scoring (an older cached response), projectRoom
  // returns null inside the adapter and the whole stat is omitted rather than
  // projected under a lens the league never chose.
  const room = useMemo(() => sleeperRoomSource(data), [data]);
  const projPlayerCount = useMemo(
    () => (room.projTeamCount > 0 ? projectionIndex().size : 0),
    [room.projTeamCount]
  );

  // 🔴 THE SEAT IS GRADED THE SAME WAY A PASTED ONE IS: rateTeam, against the
  // corpus, from a house-shaped league the adapter builds out of Sleeper's slot
  // names and scoring block. Not a second grading path — the same function the
  // team tab has always used, handed a roster it did not have to be typed.
  const seatReport = useMemo(() => {
    if (seat === null) return null;
    const team = r.leaderboard[seat];
    if (!team) return null;
    return rateTeam({
      settings: seatSettingsFromSleeper(r, data.scoring, data.leagueName),
      entries: seatEntriesFromSleeper(team),
    });
  }, [seat, r, data.scoring, data.leagueName]);

  function share() {
    const url = `${window.location.origin}/rate?league=${data.leagueId}`;
    try {
      navigator.clipboard.writeText(url).then(
        () => setCopied(true),
        () => setCopied(false)
      );
    } catch {
      /* ignore */
    }
  }

  if (seatReport) {
    const team = r.leaderboard[seat];
    return (
      <div>
        {/* 🔴 THE WAY BACK IS THE FIRST THING ON THE PAGE. Arriving here from
            the room means the league is the context and this team is the
            detail, exactly as it does on the pasted path — so the same
            affordance, in the same place, with the same words. */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
          <button type="button" data-testid="back-to-league" onClick={() => setSeat(null)} style={backBtn}>
            ‹ Back to the league
          </button>
        </div>
        <div style={{ ...fineText, marginTop: 0, marginBottom: 10 }}>
          {team.name}, measured against real 2026 auction teams rather than against the other{" "}
          {r.teams - 1} seats in this room. The letter and the badges on the league page are that
          room ranking; the percentiles below are the corpus.
        </div>
        <TeamReportView
          report={seatReport}
          leagueName={data.leagueName}
          /* Which door this reader came in by, for the gate event. */
          source="sleeper"
          /* No entry form sits behind a fetched seat, so no edit affordance is
             offered. See TeamReportView: the button is absent, not dead. */
          onEdit={null}
          onOpenLeague={() => setSeat(null)}
        />
      </div>
    );
  }

  return (
    <RoomReport
      room={room}
      viewSource="sleeper"
      onOpenTeam={(i) => setSeat(i)}
      subline={
        <>
          {r.benchmarkLabel}
          {r.benchmarkFormat === "1qb" &&
            ` · single QB benchmarks come from ${r.nRooms} real rooms so far: read as direction`}
          {r.format === "2qb" && " · graded against superflex prices, the closest cell to 2QB"}
        </>
      }
      actions={
        <>
          <button
            onClick={share}
            style={{
              padding: "10px 16px",
              fontSize: SZ.small,
              fontWeight: 800,
              borderRadius: theme.radius.lg,
              border: "none",
              background: C.brand.gradientBtn,
              color: "#fff",
              cursor: "pointer",
            }}
          >
            {copied ? "Link copied" : "Copy link for your league"}
          </button>
          <button onClick={onReset} style={roomNavBtn}>
            Rate another league
          </button>
        </>
      }
      methodology={
        room.projTeamCount > 0 ? (
          <>
            Projected points come from Mock1 house projections ({projPlayerCount} players) scored
            with your league's own scoring settings, started in the best lineup your roster slots
            allow. K and DEF use standard kicker and defense scoring. A player the projection set
            does not carry shows a dash. Positional strength ranks each position's projected points
            within this room: the sum of a team's best projected players at that position, counting
            one more than that position's own starting slots. Open a team and its roster draws as
            position cards, starters as the lineup filler placed them and then the bench. Every
            badge on those cards is that room ranking, strong, solid or thin against the other{" "}
            {room.projTeamCount} teams in this auction, and never a percentile against teams outside
            it. FLEX, SUPERFLEX, kicker, defense and bench cards show their players with a dash
            where the badge would be, because this room ranks the four positions above and nothing
            else. "Rate this team" opens that seat measured against the corpus instead.
          </>
        ) : null
      }
      footnote={
        <>
          Grades compare you to your own room: auction money is close to zero-sum, so the surplus
          leaderboard is the curve and nobody gets an F. Per-pick surplus is the market price minus
          what you paid, in your league's dollars.
        </>
      }
    >
      <EmailAsk report={r} />

      {/* 🔴 A STATEMENT, NOT AN ASK (Sep 3 2026). This line used to end by
          pointing somebody at /submit to hand over a room by hand. Every route
          into this page now contributes on its own - a pasted draft and a
          hand-built room both send the moment they grade - so there is nothing
          left to ask for, and the same words the seat report uses say what
          already happened. */}
      <div style={{ marginTop: 16, fontSize: SZ.micro, color: C.text.faint, lineHeight: 1.5 }}>
        Graded rooms join the anonymous market data.
      </div>
    </RoomReport>
  );
}
